import mqtt from 'mqtt'

import { assert, Cache, getNonEmptyArrayLastElement, isArrayNotEmpty, type ReadonlyNonEmptyArray } from '../../utils'

import { ConnectwareError, ConnectwareErrorType, type Topic } from '../../domain'

export class MqttTopic implements Topic {
    private static readonly levelsCache = new Cache<readonly string[], readonly [path: string | null]>(([path]) => `${String(path === null)}_${String(path)}`)

    private static readonly isExactCache = new Cache<boolean, readonly [path: string]>(([path]) => path)

    private static readonly isValidCache = new Cache<boolean, readonly [path: string, depth: number]>(([path, depth]) => `${path}_${depth}`)

    private static readonly isSourceCache = new Cache<boolean, readonly [topic: string, source: string]>(
        (
            /**
             * The key here is generated using the invalid markup,
             * as those can't be used in real topics,
             * making our serialization much easier
             */
            [path, source]
        ) => `${path}_${this.invalidSymbolMarkup}_${source}`
    )

    private static readonly isPrefixedCache = new Cache<boolean, [prefix: string, source: string]>(
        (
            /**
             * The key here is generated using the invalid markup,
             * as those can't be used in real topics,
             * making our serialization much easier
             */
            [prefix, source]
        ) => `${prefix}_${this.invalidSymbolMarkup}_${source}`
    )

    private static readonly levelDelimiter = '/'

    private static readonly multipleLineMatchMarkup = '#'

    private static readonly singleLineMatchMarkup = '+'

    /**
     * Topic Names and Topic Filters MUST NOT include the null character (Unicode U+0000)
     * @see https://docs.oasis-open.org/mqtt/mqtt/v3.1.1/os/mqtt-v3.1.1-os.html#_Toc384800482
     */
    private static readonly invalidSymbolMarkup = '\u0000'

    /**
     * Splits the given topic & source into their parts
     * If it detect that there is multi-level wildcard at the source,
     * Then, it removes everything after that index & returns only the parts before that multi-level wildcard index
     */
    private static getNonMultiLevelWildcardTopicParts (topic: string, base: string): readonly [topicParts: readonly string[], sourceParts: readonly string[]] {
        /**
         * Split everything by levels
         */
        let topicParts = this.fromPath(topic)
        let sourceParts = this.fromPath(base)

        /**
         * There can be only one # (@see https://upload.wikimedia.org/wikipedia/en/b/bc/Highlander_1986%2Cposter.jpg)
         * It has to be the last char in order for the sub to be valid
         * So if we find it, we remove everything after it
         */
        const wildcardPosition = sourceParts.indexOf(this.multipleLineMatchMarkup)
        if (wildcardPosition >= 0) {
            sourceParts = sourceParts.slice(0, wildcardPosition)
            topicParts = topicParts.slice(0, wildcardPosition)
        }

        return [topicParts, sourceParts]
    }

    /**
     * Match every source & topic parts by level
     * Returns true, if it detect single-level wildcard at the source part, or topic & source part are same
     */
    private static doNonMultiLevelPartsMatch (a: readonly string[], b: readonly string[]): boolean {
        return a.every((topicPart, index) => {
            const sourcePart = b[index]

            /**
             * Now we handle one level deep wildcards (+)
             * There can be any amount
             */
            return sourcePart === this.singleLineMatchMarkup || sourcePart === topicPart
        })
    }

    private static isExact (topic: string): boolean {
        return this.isExactCache.get(
            () => [this.singleLineMatchMarkup, this.multipleLineMatchMarkup].every((symbol) => !this.fromPath(topic).includes(symbol)),
            topic
        )
    }

    private static isValid (topic: string, maxDepth: number): boolean {
        return this.isValidCache.get(
            () => {
                /**
                 * All Topic Names and Topic Filters MUST be at least one character long
                 * Topic Names and Topic Filters are UTF-8 encoded strings, they MUST NOT encode to more than 65535 bytes
                 * @see https://docs.oasis-open.org/mqtt/mqtt/v3.1.1/os/mqtt-v3.1.1-os.html#_Toc384800482
                 */
                if (topic.length <= 0 || topic.length > 65_535) {
                    return false
                }

                /**
                 * Topic Names and Topic Filters MUST NOT include the null character (Unicode U+0000)
                 * @see https://docs.oasis-open.org/mqtt/mqtt/v3.1.1/os/mqtt-v3.1.1-os.html#_Toc384800482
                 */
                if (topic.includes(this.invalidSymbolMarkup)) {
                    return false
                }

                const levels = this.fromPath(topic)
                if (levels.length > maxDepth) {
                    /**
                     * This topic is too deep
                     */
                    return false
                }

                return mqtt.validateTopic(topic)
            },
            topic,
            maxDepth
        )
    }

    private static isSource (topic: string, source: string): boolean {
        return this.isSourceCache.get(
            () => {
                if (topic === source) {
                    return true
                }

                const [topicParts, sourceParts] = this.getNonMultiLevelWildcardTopicParts(topic, source)

                if (topicParts.length !== sourceParts.length) {
                    /**
                     * A mismatch in sizes is not expected from this point on
                     * As any-levels wildcards have been done away with
                     */
                    return false
                }

                return this.doNonMultiLevelPartsMatch(topicParts, sourceParts)
            },
            topic,
            source
        )
    }

    private static isPrefixedBy (prefixTopic: string, topic: string): boolean {
        return this.isPrefixedCache.get(
            () => {
                if (prefixTopic === topic) {
                    return true
                }

                const [prefixParts, topicParts] = this.getNonMultiLevelWildcardTopicParts(prefixTopic, topic)
                const endsWithSlash = !prefixParts[prefixParts.length - 1]

                return this.doNonMultiLevelPartsMatch(
                    /**
                     * Cuts off the prefix's last part if it ends with a slash
                     * unless the prefix is shorter than the source and thus
                     * needs another level becomes relevant again
                     */
                    prefixParts.slice(0, prefixParts.length - Number(endsWithSlash && prefixParts.length <= topicParts.length)),
                    /**
                     * Cut of to the level of the source without the trailing slash
                     */
                    topicParts.slice(0, prefixParts.length - Number(endsWithSlash))
                )
            },
            prefixTopic,
            topic
        )
    }

    private static fromPath (topic: Topic['fullPath']): readonly string[] {
        return this.levelsCache.get(
            /**
             * An Mqtt topic can **not** be an empty string, but levels beyond the first one can
             * So when calculating the levels, and there is nothing above the given path (IE isForChild === false)
             * Then yield no levels, otherwise, yield an empty set of levels
             *
             * @see https://docs.oasis-open.org/mqtt/mqtt/v3.1.1/os/mqtt-v3.1.1-os.html#_Toc384800482
             */
            () => (topic !== null ? topic.split(MqttTopic.levelDelimiter) : []),
            topic
        )
    }

    private static toPath (...levels: readonly string[]): string {
        return levels.join(this.levelDelimiter)
    }

    constructor (
        /**
         * Null value is used to denominate a root node that doesn't have value on its own
         */
        readonly fullPath: string | null,
        private readonly maxDepth: number | null
    ) {}

    get levels (): Topic['levels'] {
        return this.withFullPath((fullPath) => MqttTopic.fromPath(fullPath), null)
    }

    get lastLevel (): Topic['lastLevel'] {
        return this.levels && isArrayNotEmpty(this.levels) ? getNonEmptyArrayLastElement(this.levels) : null
    }

    get valid (): Topic['valid'] {
        const { maxDepth } = this
        assert(maxDepth !== null, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Invalid max topic depth provided'))
        return this.withFullPath((fullPath) => MqttTopic.isValid(fullPath, maxDepth), false)
    }

    get exact (): Topic['exact'] {
        return this.withFullPath((fullPath) => MqttTopic.isExact(fullPath), null)
    }

    private withFullPath<T> (transformer: (fullPath: string) => T, withNoPath: T): T {
        return this.fullPath === null ? withNoPath : transformer(this.fullPath)
    }

    createChild (childTopic: string): ReturnType<Topic['createChild']> {
        const levels = MqttTopic.fromPath(this.fullPath)

        /**
         * The function fromPath should yield at least a single sized array
         * Even if it contains only an empty string
         */
        const [firstLevel, ...restLevels] = MqttTopic.fromPath(childTopic) as ReadonlyNonEmptyArray<string>

        return [new MqttTopic(MqttTopic.toPath(...levels, firstLevel), this.maxDepth), restLevels.length ? MqttTopic.toPath(...restLevels) : null]
    }

    isPrefixedBy (topics: readonly string[]): ReturnType<Topic['isPrefixedBy']> {
        return this.withFullPath((fullPath) => topics.some((topic) => MqttTopic.isPrefixedBy(fullPath, topic)), false)
    }

    isSourcedFrom (topics: readonly string[]): ReturnType<Topic['isSourcedFrom']> {
        return this.withFullPath((fullPath) => topics.some((source: string) => MqttTopic.isSource(fullPath, source)), false)
    }

    filterSources (topics: readonly string[]): readonly string[] {
        return this.withFullPath((fullPath) => topics.filter((source) => MqttTopic.isSource(fullPath, source)), [])
    }
}
